import { useState, useEffect, useCallback } from "react";
import {
  useUpdateDocumento,
  useDocumentos,
  useDeleteDocumento,
  type Documento,
} from "@/hooks/useDocumentos";
import { useDocumentUpload } from "@/hooks/documentos/useDocumentUpload";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  File,
  Trash2,
  Search,
  Download,
  BookOpen,
  Brain,
  MoreVertical,
  Eye,
  RefreshCw,
  Loader2,
} from "lucide-react";
import {
  DocumentDropzone,
  type FileWithStatus,
} from "@/components/ui/DocumentDropzone";
import {
  trainWithFileKey,
  downloadPrivateObject,
} from "@retro-ia/shared/upload";
import { toast } from "sonner";
import { axiosInstance } from "@retro-ia/shared/axios";
import { SectionHeader } from "@/components/layout/SectionHeader";
import { DocumentCard } from "@/features/programas/components/DocumentCard";
import { DocumentStats } from "@/features/programas/components/DocumentStats";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

// Helper functions
const filterAndSortDocuments = (
  documentos: any[],
  files: any[],
  search: string,
  statusFilter: string,
  sortBy: string,
) => {
  let filtered = documentos;

  if (search) {
    filtered = filtered.filter((doc) =>
      doc.archivo.nombre.toLowerCase().includes(search.toLowerCase()),
    );
  }

  if (statusFilter !== "all") {
    filtered = filtered.filter((doc) => {
      const fileStatus = files.find((f: any) => f.id === doc.id);
      const status = fileStatus?.status || "completed";
      return status === statusFilter;
    });
  }

  filtered.sort((a, b) => {
    switch (sortBy) {
      case "name-asc":
        return a.archivo.nombre.localeCompare(b.archivo.nombre);
      case "name-desc":
        return b.archivo.nombre.localeCompare(a.archivo.nombre);
      case "date-desc":
        return (
          new Date(b.archivo.createdAt || 0).getTime() -
          new Date(a.archivo.createdAt || 0).getTime()
        );
      case "date-asc":
        return (
          new Date(a.archivo.createdAt || 0).getTime() -
          new Date(b.archivo.createdAt || 0).getTime()
        );
      case "size-desc":
        return (b.archivo.size || 0) - (a.archivo.size || 0);
      case "size-asc":
        return (a.archivo.size || 0) - (b.archivo.size || 0);
      default:
        return 0;
    }
  });

  return filtered;
};

const getDocumentStats = (documentos: any[], files: any[]) => {
  const stats = {
    total: documentos.length,
    completed: 0,
    processing: 0,
    error: 0,
    totalSize: 0,
  };

  documentos.forEach((doc) => {
    const fileStatus = files.find((f: any) => f.id === doc.id);
    const status = fileStatus?.status || "completed";

    if (status === "completed") stats.completed++;
    else if (status === "processing" || status === "uploading")
      stats.processing++;
    else if (status === "error") stats.error++;

    stats.totalSize += doc.archivo.size || 0;
  });

  return stats;
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "—";
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = (bytes / Math.pow(1024, i)).toFixed(1);
  return `${size} ${sizes[i]}`;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

interface ProgramaDocumentosProps {
  idPrograma: number;
  cerebro: string;
  onRefetchPrograma: () => void;
}

export function ProgramaDocumentos({
  idPrograma,
  cerebro,
  onRefetchPrograma,
}: ProgramaDocumentosProps) {
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [documentSearch, setDocumentSearch] = useState("");
  const [documentStatusFilter, setDocumentStatusFilter] = useState<
    "all" | "completed" | "processing" | "error"
  >("all");
  const [documentSortBy, setDocumentSortBy] = useState<
    | "name-asc"
    | "name-desc"
    | "date-desc"
    | "date-asc"
    | "size-desc"
    | "size-asc"
  >("date-desc");
  const [documentViewMode, setDocumentViewMode] = useState<"grid" | "list">(
    "grid",
  );
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<number>>(
    new Set(),
  );
  const [documentDetail, setDocumentDetail] = useState<Documento | null>(null);
  const [isReindexing, setIsReindexing] = useState(false);

  const { mutateAsync: updateDocumentoAsync } = useUpdateDocumento();

  // Inicializar modo de vista desde localStorage
  useEffect(() => {
    const savedViewMode = localStorage.getItem("documentViewMode") as
      | "grid"
      | "list"
      | null;
    if (savedViewMode) {
      setDocumentViewMode(savedViewMode);
    }
  }, []);

  const {
    data: documentosData,
    isLoading: isLoadingDocumentos,
    refetch: refetchDocumentos,
  } = useDocumentos({
    idPrograma,
    limit: 100,
  });
  const { mutate: deleteDocumento } = useDeleteDocumento({
    idPrograma,
  });

  const docUpload = useDocumentUpload({
    idPrograma,
    cerebro,
    onError: (fileName, error) => {
      setFiles((prev) =>
        prev.map((f) =>
          f.name === fileName
            ? { ...f, status: "error", message: error }
            : f,
        ),
      );
      toast.error(`Error al subir ${fileName}: ${error}`, { duration: 5000 });
    },
  });

  // Convertir documentos de BD a FileWithStatus
  const documentosToFiles = useCallback(
    (documentos: Documento[]): FileWithStatus[] => {
      return documentos.map((doc) => {
        let status: FileWithStatus["status"] = "completed";

        if (!doc.cargado) {
          status = "processing";
        } else if (doc.mensaje.toLowerCase().includes("error")) {
          status = "error";
        } else {
          status = "completed";
        }

        return {
          id: doc.id,
          name: doc.archivo.nombre,
          status,
          message: doc.mensaje,
          url: doc.archivo.url,
        };
      });
    },
    [],
  );

  // Sincronizar archivos con documentos de BD
  useEffect(() => {
    if (!documentosData?.data) return;

    const existingFiles = documentosToFiles(documentosData.data);

    setFiles((prevFiles) => {
      const pendingFiles = prevFiles.filter(
        (f) =>
          f.status === "pending" ||
          f.status === "uploading" ||
          f.status === "processing",
      );

      const combinedFiles = [
        ...existingFiles,
        ...pendingFiles.filter(
          (pf) => !existingFiles.some((ef) => ef.name === pf.name),
        ),
      ];

      return combinedFiles;
    });
  }, [documentosData?.data, documentosToFiles]);

  // Polling para verificar estado de procesamiento
  useEffect(() => {
    const hasProcessingFiles = files.some(
      (f) => f.status === "processing" || f.status === "uploading",
    );

    if (!hasProcessingFiles) return;

    const interval = setInterval(() => {
      refetchDocumentos();
    }, 5000);

    return () => clearInterval(interval);
  }, [files, refetchDocumentos]);

  const handleDownload = async (doc: Documento) => {
    const result = await downloadPrivateObject({
      key: doc.archivo.llave,
    });
    if (result.url) {
      window.open(result.url, "_blank");
    }
  };

  const handleUpload = async (uploadedFiles: File[]) => {
    const pdfFiles = uploadedFiles.filter(
      (file) =>
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf"),
    );
    if (pdfFiles.length !== uploadedFiles.length) {
      toast.error(
        "Solo se permiten archivos PDF. Por favor, selecciona solo archivos PDF.",
      );
      return;
    }

    const fileNames = uploadedFiles.map((f) => f.name);
    setFiles((prev) =>
      prev.map((f) =>
        fileNames.includes(f.name) && f.status === "pending"
          ? { ...f, status: "uploading" }
          : f,
      ),
    );

    const { successCount, errorCount } =
      await docUpload.uploadFiles(uploadedFiles);

    await refetchDocumentos();
    await onRefetchPrograma();

    if (successCount > 0) {
      toast.success(
        `${successCount} archivo${successCount > 1 ? "s" : ""} subido${successCount > 1 ? "s" : ""} correctamente`,
      );
    }
    if (errorCount > 0 && successCount === 0) {
      toast.error(
        `No se pudieron subir ${errorCount} archivo${errorCount > 1 ? "s" : ""}. Revisa los errores arriba.`,
      );
    }
  };

  const handleRetry = async (doc: Documento) => {
    try {
      await trainWithFileKey({
        key: doc.archivo.llave,
        brain: cerebro,
        documentId: doc.id,
        fileName: doc.archivo.nombre,
      });
      await updateDocumentoAsync({
        id: doc.id,
        cargado: true,
        mensaje: "Archivo cargado correctamente",
      });
      await refetchDocumentos();
      toast.success("Documento reprocesado correctamente");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error al reintentar";
      toast.error(errorMessage, { duration: 5000 });
      try {
        await updateDocumentoAsync({
          id: doc.id,
          cargado: true,
          mensaje: errorMessage,
        });
      } catch {
        // ignore
      }
      await refetchDocumentos();
    }
  };

  const handleDelete = async (fileId: number) => {
    const fileToDelete = files.find((f) => f.id === fileId);
    if (!fileToDelete) {
      toast.error("Archivo no encontrado");
      return;
    }

    const confirmed = window.confirm(
      `¿Estás seguro de que deseas eliminar "${fileToDelete.name}"?`,
    );

    if (!confirmed) return;

    deleteDocumento(fileId, {
      onSuccess: () => {
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
        onRefetchPrograma();
      },
      onError: (error) => {
        console.error("Error al eliminar documento:", error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Error desconocido al eliminar el archivo";
        toast.error(`Error al eliminar ${fileToDelete.name}: ${errorMessage}`, {
          duration: 5000,
        });
      },
    });
  };

  return (
    <section className="space-y-6">
      <SectionHeader
        icon={Brain}
        title="Base de conocimiento"
        description="Carga y gestiona los documentos PDF que alimentarán el cerebro de IA del programa. Estos archivos sirven como fuente de información para generar respuestas y apoyar a los aprendices."
      />

      {/* Estadísticas de documentos */}
      {documentosData?.data && documentosData.data.length > 0 && (
        <>
          <DocumentStats
            totalDocuments={documentosData.data.length}
            completedDocuments={
              getDocumentStats(documentosData.data, files).completed
            }
            processingDocuments={
              getDocumentStats(documentosData.data, files).processing
            }
            errorDocuments={
              getDocumentStats(documentosData.data, files).error
            }
            totalSize={getDocumentStats(documentosData.data, files).totalSize}
          />
          <div className="flex justify-end">
            <Button
              variant="danger"
              size="sm"
              disabled={isReindexing}
              onClick={async () => {
                if (
                  !window.confirm(
                    "Se borrarán todos los vectores y se volverán a indexar los documentos. ¿Continuar?",
                  )
                )
                  return;
                setIsReindexing(true);
                try {
                  await axiosInstance.post(
                    `/programas/${idPrograma}/reindex-knowledge-base`,
                  );
                  await refetchDocumentos();
                  toast.success(
                    "Base de conocimiento reindexada correctamente",
                  );
                } catch (err) {
                  const msg =
                    err instanceof Error ? err.message : "Error al reindexar";
                  toast.error(msg, { duration: 5000 });
                } finally {
                  setIsReindexing(false);
                }
              }}
            >
              {isReindexing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reindexando...
                </>
              ) : (
                "Vaciar y reindexar base de conocimiento"
              )}
            </Button>
          </div>
        </>
      )}

      {/* Área de subida */}
      <div className="rounded-2xl border border-dashed border-black/10 bg-[var(--card-background)]/40 p-4 dark:border-white/10">
        <DocumentDropzone
          files={files}
          onFilesChange={setFiles}
          onUpload={handleUpload}
          onDelete={handleDelete}
          accept="application/pdf"
          hideFilesList={true}
        />
      </div>

      <Separator className="my-6" />

      {/* Barra de búsqueda y filtros */}
      {documentosData?.data && documentosData.data.length > 0 && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar documentos..."
                value={documentSearch}
                onChange={(e) => setDocumentSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select
              value={documentStatusFilter}
              onValueChange={(value: any) => setDocumentStatusFilter(value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="completed">Completados</SelectItem>
                <SelectItem value="processing">Procesando</SelectItem>
                <SelectItem value="error">Con errores</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={documentSortBy}
              onValueChange={(value: any) => setDocumentSortBy(value)}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date-desc">Más recientes</SelectItem>
                <SelectItem value="date-asc">Más antiguos</SelectItem>
                <SelectItem value="name-asc">Nombre A-Z</SelectItem>
                <SelectItem value="name-desc">Nombre Z-A</SelectItem>
                <SelectItem value="size-desc">Tamaño mayor</SelectItem>
                <SelectItem value="size-asc">Tamaño menor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            {/* Toggle vista */}
            <div className="flex items-center border rounded-lg p-1">
              <Button
                variant={documentViewMode === "grid" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setDocumentViewMode("grid");
                  localStorage.setItem("documentViewMode", "grid");
                }}
                className="h-7 w-7 px-2"
              >
                <div className="grid grid-cols-2 gap-0.5">
                  <div className="h-1 w-1 bg-current"></div>
                  <div className="h-1 w-1 bg-current"></div>
                  <div className="h-1 w-1 bg-current"></div>
                  <div className="h-1 w-1 bg-current"></div>
                </div>
              </Button>
              <Button
                variant={documentViewMode === "list" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setDocumentViewMode("list");
                  localStorage.setItem("documentViewMode", "list");
                }}
                className="h-7 w-7 px-2"
              >
                <div className="space-y-0.5">
                  <div className="h-0.5 w-3 bg-current"></div>
                  <div className="h-0.5 w-3 bg-current"></div>
                  <div className="h-0.5 w-3 bg-current"></div>
                </div>
              </Button>
            </div>

            {/* Botón de limpiar filtros */}
            {(documentSearch || documentStatusFilter !== "all") && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDocumentSearch("");
                  setDocumentStatusFilter("all");
                  setDocumentSortBy("date-desc");
                }}
              >
                Limpiar filtros
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Acciones en masa */}
      {selectedDocumentIds.size > 0 && (
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">
              {selectedDocumentIds.size} seleccionados
            </Badge>
            <Button
              size="sm"
              onClick={() => setSelectedDocumentIds(new Set())}
              variant="outline"
            >
              Deseleccionar todos
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                if (
                  confirm(
                    `¿Estás seguro de que deseas eliminar los ${selectedDocumentIds.size} documentos seleccionados?`,
                  )
                ) {
                  Array.from(selectedDocumentIds).forEach((id) => {
                    handleDelete(id);
                  });
                  setSelectedDocumentIds(new Set());
                }
              }}
            >
              Eliminar seleccionados
            </Button>
          </div>
        </div>
      )}

      {isLoadingDocumentos ? (
        <div className="flex items-center justify-center rounded-2xl border border-black/5 bg-[var(--card-background)] py-8 dark:border-white/10">
          <Skeleton className="h-6 w-48" />
        </div>
      ) : documentosData?.data && documentosData.data.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[var(--foreground)]">
              Documentos cargados
            </h3>
            <span className="text-sm text-muted-foreground">
              {
                filterAndSortDocuments(
                  documentosData.data,
                  files,
                  documentSearch,
                  documentStatusFilter,
                  documentSortBy,
                ).length
              }{" "}
              de {documentosData.data.length} documentos
            </span>
          </div>

          <div
            className={cn(
              "max-h-[600px] overflow-y-auto pr-1",
              documentViewMode === "grid"
                ? "grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                : "space-y-3",
            )}
          >
            {filterAndSortDocuments(
              documentosData.data,
              files,
              documentSearch,
              documentStatusFilter,
              documentSortBy,
            ).map((doc: Documento) => {
              const fileStatus = files.find((f: any) => f.id === doc.id);
              const isSelected = selectedDocumentIds.has(doc.id);

              if (documentViewMode === "list") {
                return (
                  <div
                    key={doc.id}
                    className={cn(
                      "flex items-center gap-4 p-4 rounded-lg border border-black/5 bg-[var(--card-background)] hover:bg-muted/50 transition-colors",
                      isSelected &&
                        "ring-2 ring-primary/20 border-primary/30",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const newSelected = new Set(selectedDocumentIds);
                        if (e.target.checked) {
                          newSelected.add(doc.id);
                        } else {
                          newSelected.delete(doc.id);
                        }
                        setSelectedDocumentIds(newSelected);
                      }}
                      className="h-4 w-4"
                    />
                    <File className="h-8 w-8 text-muted-foreground" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-[var(--foreground)] truncate">
                        {doc.archivo.nombre}
                      </h4>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                        <span>{formatFileSize(doc.archivo?.size)}</span>
                        <span>{formatDate(doc.creadoEl)}</span>
                        <Badge variant="outline" className="text-xs">
                          {fileStatus?.status || "completed"}
                        </Badge>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="icon"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => setDocumentDetail(doc)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Ver detalles
                          </DropdownMenuItem>
                          {(fileStatus?.status || "completed") === "error" && (
                            <DropdownMenuItem
                              onClick={() => handleRetry(doc)}
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Reintentar
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDownload(doc)}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Descargar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDelete(doc.id)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              } else {
                return (
                  <DocumentCard
                    key={doc.id}
                    id={doc.id}
                    name={doc.archivo.nombre}
                    status={fileStatus?.status || "completed"}
                    size={doc.archivo?.size ?? 0}
                    uploadDate={doc.creadoEl}
                    url={doc.archivo.url}
                    message={fileStatus?.message}
                    onView={() => setDocumentDetail(doc)}
                    onRetry={
                      (fileStatus?.status || "completed") === "error"
                        ? () => handleRetry(doc)
                        : undefined
                    }
                    onDownload={() => handleDownload(doc)}
                    onDelete={() => handleDelete(doc.id)}
                    selected={isSelected}
                    onSelect={(checked) => {
                      const newSelected = new Set(selectedDocumentIds);
                      if (checked) {
                        newSelected.add(doc.id);
                      } else {
                        newSelected.delete(doc.id);
                      }
                      setSelectedDocumentIds(newSelected);
                    }}
                    showCheckbox={selectedDocumentIds.size > 0}
                  />
                );
              }
            })}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-black/10 bg-[var(--background-muted-secondary)]/60 px-6 py-8 text-center">
          <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground mb-2">
            No hay archivos cargados
          </p>
          <p className="text-xs text-muted-foreground">
            Sube documentos PDF para enriquecer la base de conocimiento del
            programa
          </p>
        </div>
      )}

      {/* Modal Ver detalle de documento */}
      <Dialog
        open={!!documentDetail}
        onOpenChange={(open) => !open && setDocumentDetail(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <File className="h-5 w-5" />
              Detalle del documento
            </DialogTitle>
          </DialogHeader>
          {documentDetail && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Nombre
                </p>
                <p className="text-sm break-words">
                  {documentDetail.archivo.nombre}
                </p>
              </div>
              <div className="flex gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Tamaño
                  </p>
                  <p className="text-sm">
                    {formatFileSize(documentDetail.archivo?.size)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Fecha de carga
                  </p>
                  <p className="text-sm">
                    {formatDate(documentDetail.creadoEl)}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Estado
                </p>
                <p className="text-sm">
                  {documentDetail.cargado ? "Completado" : "Procesando"}
                </p>
              </div>
              {documentDetail.mensaje && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Mensaje
                  </p>
                  <p className="text-sm">{documentDetail.mensaje}</p>
                </div>
              )}
              <Button
                className="w-full"
                variant="outline"
                onClick={() => {
                  handleDownload(documentDetail);
                  setDocumentDetail(null);
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Descargar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
